import {Ref, RefObject, useImperativeHandle, useRef} from 'react';

import i18n from 'i18next';

import {Collection} from 'components/recommendations/leo-promotions-badge/types';
import {config} from 'config/env';
import {BASKET_RECOMMENDATION_CAROUSEL_NAME, isBasketDiversifyFlagEnabled} from 'constants/recommendation';
import {ProductType} from 'types';
import {CollectionType, ShoppingDataLayerProduct} from 'types/recommedation-types/recommendations';
import {getWholeNumberOrTwoDecimal} from 'utils/common/shopify.util';

import {
  TRANSLATION_KEYS,
  PROMOTION_COLORS,
  PROPOINT_COLLECTION,
  ComplexPromotionType,
  DEFAULT
} from './proPointsPromotion.util';

export declare type ProductAvailabilityType = (typeof ProductAvailability)[keyof typeof ProductAvailability];

export declare const ProductAvailability: {
  readonly available: 'available';
  readonly outOfStock: 'outOfStock';
  readonly notDeliverable: 'notDeliverable';
  readonly unavailable: 'unavailable';
  readonly notEligible: 'notEligible';
};

export function getIdFromGraphId(graphId: unknown): string | null {
  if (typeof graphId === 'string') {
    const regexToGetId = new RegExp(/([0-9]+)/, 'g');
    const regexToGetIdEx = regexToGetId.exec(graphId);
    const id = regexToGetIdEx?.length ? regexToGetIdEx[0] : null;
    return id;
  } else {
    return null;
  }
}

export enum RecommendationType {
  PERSONALIZED = 'PERSONALIZED',
  CUSTOM = 'CUSTOM'
}

export const getBasketRecommendationTitle = (typeOfRecommendation: string) => {
  switch (typeOfRecommendation) {
    case RecommendationType.CUSTOM:
      return 'recommendations.customListTitle';
    case RecommendationType.PERSONALIZED:
      return 'recommendations.personalizedListTitle';
    default:
      return 'recommendations.customListFor3PTitle';
  }
};

export const getBasketRecommendationAnalyticTitle = (typeOfRecommendation: string) => {
  switch (typeOfRecommendation) {
    case RecommendationType.CUSTOM:
      return BASKET_RECOMMENDATION_CAROUSEL_NAME.custom;
    case RecommendationType.PERSONALIZED:
      return BASKET_RECOMMENDATION_CAROUSEL_NAME.personalised;
    default:
      return BASKET_RECOMMENDATION_CAROUSEL_NAME.custom3p;
  }
};

export type SearchCarouselURLParams = {
  userId?: string;
  productVariant?: string;
  isExpressMode?: boolean;
  sameDay?: boolean;
  title?: string;
  accessToken?: string;
  recommendationVariant?: string;
  stores: string[];
  platform?: string;
  isDiversify?: boolean;
};

export const geSearchCarouselUrl = (params: SearchCarouselURLParams) => {
  const queryParams = [
    `type=basketRecommendation`,
    `locale=${i18n?.language || 'th'}`,
    params.stores?.length > 0 && `storeCodes=${params.stores.map((s) => s).join(',')}`,
    params?.userId && `userId=${encodeURIComponent(params.userId)}`,
    params?.accessToken && `accessToken=${encodeURIComponent(params.accessToken)}`,
    params?.productVariant && `productVariant=${params.productVariant}`,
    params?.sameDay && `sameDay=${params.sameDay}`,
    params?.title && `title=${encodeURIComponent(params.title)}`,
    params?.recommendationVariant && `recommendationVariant=${params.recommendationVariant}`,
    params?.platform && `platform=${params.platform}`,
    params?.isDiversify && `isDiversify=${params.isDiversify}`,
    `isDiversify=${isBasketDiversifyFlagEnabled}`,
    `isExpressMode=${params.isExpressMode}`
  ]
    .filter(Boolean)
    .join('&');

  const baseUrl = config.CAROUSEL_MFE_URL;

  return `${baseUrl}?${queryParams}`;
};

export const findNearestValueByMOQ = (moq: number, inputValue: number) => {
  const nearestMoqValue = Math.round(inputValue / moq) * moq;

  if (nearestMoqValue === 0 && inputValue !== 0) {
    return moq;
  }

  return getWholeNumberOrTwoDecimal(nearestMoqValue);
};

export const getPromotionTagStyle = (type: string) => {
  let tagString = TRANSLATION_KEYS.PROPOINT.TAG;
  let desc = TRANSLATION_KEYS.PROPOINT.DESCRIPTION;
  let bgColor = PROMOTION_COLORS.PROPOINT.BACKGROUND;
  let txtColor = PROMOTION_COLORS.PROPOINT.TEXT;

  switch (type) {
    case PROPOINT_COLLECTION:
      tagString = TRANSLATION_KEYS.PROPOINT.TAG;
      desc = TRANSLATION_KEYS.PROPOINT.DESCRIPTION;
      bgColor = PROMOTION_COLORS.PROPOINT.BACKGROUND;
      txtColor = PROMOTION_COLORS.PROPOINT.TEXT;
      break;
    case ComplexPromotionType.MULTI_BUY:
      tagString = TRANSLATION_KEYS.MULTI_BUY.TAG;
      desc = TRANSLATION_KEYS.MULTI_BUY.DESCRIPTION;
      bgColor = PROMOTION_COLORS.DEFAULT.BACKGROUND;
      txtColor = PROMOTION_COLORS.DEFAULT.TEXT;
      break;
    case ComplexPromotionType.MIX_MATCH:
      tagString = TRANSLATION_KEYS.MIX_MATCH.TAG;
      bgColor = PROMOTION_COLORS.DEFAULT.BACKGROUND;
      txtColor = PROMOTION_COLORS.DEFAULT.TEXT;
      break;
    case DEFAULT:
      tagString = `loyalty.proPointPromotions.complexPromotion.${type}`;
      bgColor = PROMOTION_COLORS.DEFAULT.BACKGROUND;
      txtColor = PROMOTION_COLORS.DEFAULT.TEXT;
      break;
    default:
      tagString = `loyalty.proPointPromotions.complexPromotion.${type}`;
      bgColor = PROMOTION_COLORS.DEFAULT.BACKGROUND;
      txtColor = PROMOTION_COLORS.DEFAULT.TEXT;
      break;
  }

  return {
    tagString,
    desc,
    bgColor,
    txtColor
  };
};

export interface shoppingProductGetPromotionRibbonsReturnType {
  productType: ProductType;
  isBulky: boolean;
  rightRibbonInfo: ShoppingDataLayerProduct['collections'];
  promotionInfoWithUrl: Collection;
  isPreOrder: boolean;
  marketingBadge:
    | (Collection & {
        accessibilityId: string;
      })
    | null;
}

export const shoppingIs1PMakroStore = (storeCode: string | undefined): boolean =>
  Boolean(storeCode && !storeCode.includes('3P'));

export const shoppingProductGetPromotionRibbons = (
  product: ShoppingDataLayerProduct
): shoppingProductGetPromotionRibbonsReturnType => {
  const storeCode = product?.variants[0]?.storeCode;
  const productType = shoppingIs1PMakroStore(storeCode) ? ProductType.PRODUCT_1P : ProductType.PRODUCT_3P;

  const rightRibbonInfo = product?.collections?.filter((item) => item.type === CollectionType.IMAGE_RIGHT_RIBBON) || [];
  const promotionInfoWithUrl =
    product?.collections?.find((item) => item.type === CollectionType.PROMOTION && item.imageUrl) || [];

  const isBulky = product?.isBulky || false;
  const isPreOrder = product?.isPreOrder || false;

  const getAccessibilityIdWithPrefix = (prefix: string, title: string): string => {
    return `${prefix}_${title}`;
  };

  const getMarketingBadge = (): (Collection & {accessibilityId: string}) | null => {
    if (!product.collections?.length) {
      return null;
    }

    const primary = product.collections.filter((c) => c.type === CollectionType.PRIMARY);
    const secondary = product.collections.filter((c) => c.type === CollectionType.SECONDARY);
    const loyalty = product.collections.filter((c) => c.type === CollectionType.LOYALTY);

    const winner = primary.length
      ? pickByDisplayOrder(primary)
      : secondary.length
        ? pickByDisplayOrder(secondary)
        : loyalty.length
          ? pickByDisplayOrder(loyalty)
          : null;

    if (!winner) {
      return null;
    }

    return {
      ...winner,
      accessibilityId: getAccessibilityIdWithPrefix(winner.type?.toLowerCase(), winner.title)
    };
  };

  function pickByDisplayOrder(collections: Collection[]): Collection | null {
    if (collections.length === 0) {
      return null;
    }

    return collections.reduce((best, curr) => {
      return (curr.displayOrder ?? Infinity) < (best.displayOrder ?? Infinity) ? curr : best;
    });
  }

  const marketingBadge = getMarketingBadge();

  return {
    productType,
    isBulky,
    rightRibbonInfo: rightRibbonInfo.slice(0, 3),
    promotionInfoWithUrl: promotionInfoWithUrl as Collection,
    isPreOrder,
    marketingBadge
  };
};

export const getDiscountPercentageText = (discount: number) => {
  if (discount < 1) {
    return;
  }

  const formattedDiscountValue = Math.trunc(discount);
  return `-${formattedDiscountValue}%`;
};

export function useDOMRef<T extends HTMLElement = HTMLElement>(ref?: RefObject<T | null> | Ref<T | null>) {
  const domRef = useRef<T>(null);

  useImperativeHandle(ref, () => domRef.current);

  return domRef;
}
